import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useLanguage } from '../context/LanguageContext';
import { apiRequest, getMediaUrl } from '../lib/api';

const BlogIndexPage = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    apiRequest('/api/blog/posts?limit=12')
      .then(setPosts)
      .catch(() => setPosts([]));
  }, []);

  const content = {
    es: {
      title: 'Illumi Journal',
      subtitle: 'Contenido premium sobre web, branding, automatización y distribución de contenido.',
      search: 'Buscar artículos',
      all: 'Todo',
      read: 'Leer artículo',
      backHome: 'Volver al inicio',
    },
    en: {
      title: 'Illumi Journal',
      subtitle: 'Premium content about web strategy, branding, automation, and content distribution.',
      search: 'Search articles',
      all: 'All',
      read: 'Read article',
      backHome: 'Back home',
    },
  };

  const t = content[language];
  const categories = useMemo(() => ['all', ...new Set(posts.flatMap((post) => post.categories || []))], [posts]);
  const filteredPosts = useMemo(
    () => posts.filter((post) => {
      const matchesCategory = activeCategory === 'all' || post.categories?.includes(activeCategory);
      const haystack = `${post.title} ${post.excerpt} ${(post.tags || []).join(' ')}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    }),
    [activeCategory, posts, search],
  );

  return (
    <section className="blog-page">
      <div className="container">
        <div className="blog-page-hero">
          <span className="section-eyebrow">Blog</span>
          <h1 data-testid="blog-index-title">{t.title}</h1>
          <p data-testid="blog-index-subtitle">{t.subtitle}</p>
          <div className="blog-page-actions">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t.search}
              data-testid="blog-search-input"
            />
            <a href="/#hero" className="btn btn-outline" data-testid="blog-back-home-link">{t.backHome}</a>
          </div>
        </div>

        <div className="blog-category-row" data-testid="blog-category-row">
          {categories.map((category) => (
            <button
              key={category}
              className={`blog-category-chip ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
              data-testid={`blog-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {category === 'all' ? t.all : category}
            </button>
          ))}
        </div>

        <div className="blog-page-grid">
          {filteredPosts.map((post) => (
            <article key={post.id} className="blog-card blog-card-large" data-testid={`blog-index-card-${post.slug}`}>
              {post.cover_media_url && (
                <div className="blog-card-media">
                  <img src={getMediaUrl(post.cover_media_url)} alt={post.title} />
                </div>
              )}
              <div className="blog-card-content">
                <div className="blog-meta-row">
                  <span>{post.author_name}</span>
                  <span>{post.categories?.join(' · ')}</span>
                </div>
                <h2>{post.title}</h2>
                <p>{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="blog-card-link" data-testid={`blog-index-read-link-${post.slug}`}>
                  {t.read}
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogIndexPage;